import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Bookmark, ArrowRight, Newspaper } from "lucide-react";

/**
 * Small dashboard tile showing a preview of her Read Weekend list.
 * Only surfaces once she has visited the News page at least once, so users
 * who don't use News don't see clutter. The visited flag is written on
 * every /news mount via localStorage["ascend_news_visited"].
 */
export default function ReadWeekendTile() {
  const [visited, setVisited] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    try { setVisited(localStorage.getItem("ascend_news_visited") === "1"); }
    catch (_) { /* private mode */ }
  }, []);

  useEffect(() => {
    if (!visited) { setLoading(false); return; }
    (async () => {
      try {
        const { data } = await api.get(`/news/saved?limit=3`);
        setItems(data.items || []);
      } catch (_) {
        // silent — tile just won't render below
      } finally {
        setLoading(false);
      }
    })();
  }, [visited]);

  if (!visited || loading) return null;

  return (
    <Link
      to="/news/saved"
      className="card-soft p-6 block group hover:-translate-y-0.5 transition-transform duration-200 fade-up delay-2"
      data-testid="dashboard-read-weekend-tile"
    >
      <div className="flex items-center gap-2 mb-2">
        <Bookmark size={14} strokeWidth={1.5} style={{ color: "var(--primary)" }} />
        <span className="eyebrow">read weekend</span>
      </div>
      <div className="flex items-baseline justify-between">
        <div className="font-display text-2xl" style={{ color: "var(--text)" }} data-testid="dashboard-read-weekend-count">
          {items.length}
          <span className="text-sm ml-1 text-muted">saved</span>
        </div>
        <ArrowRight size={16} strokeWidth={1.5} className="opacity-40 group-hover:opacity-80 transition-opacity" style={{ color: "var(--primary)" }} />
      </div>

      {items.length === 0 ? (
        <div className="mt-3 flex items-center gap-2 text-sm text-muted">
          <Newspaper size={14} strokeWidth={1.5} />
          Save articles you'd love to read on Saturday morning.
        </div>
      ) : (
        <ul className="mt-3 space-y-2">
          {items.slice(0, 2).map((it) => (
            <li key={it.article_hash} className="text-sm truncate" style={{ color: "var(--text-muted)" }} title={it.title}>
              · {it.title}
            </li>
          ))}
        </ul>
      )}
    </Link>
  );
}
